/**
 * vault #32 — browser client for "which vault folders hold company X's credentials".
 * Talks only to Hub's own `/api/vault/rpc` (same origin, the user's Hub session); metadata
 * only — the route and the vault both refuse anything else. Pure helpers are exported for
 * unit tests.
 */
import { z } from "zod";

export const CompanyFolderRow = z.object({
  folderId: z.string(),
  name: z.string(),
  path: z.array(z.string()),
  parentFolderId: z.string().nullable(),
  hubCompanyId: z.string(),
  itemCount: z.number().int(),
  topMost: z.boolean(),
  permissions: z.array(z.string()),
});
export type CompanyFolderRow = z.infer<typeof CompanyFolderRow>;

export const CompanyFoldersResult = z.object({
  hubCompanyId: z.string(),
  folders: z.array(CompanyFolderRow),
  hiddenCount: z.number().int().nullable(),
  company: z.object({ id: z.string(), name: z.string() }),
});
export type CompanyFoldersResult = z.infer<typeof CompanyFoldersResult>;

/** The subset of a listed folder the link picker needs. */
export const LinkableFolder = z.object({
  folderId: z.string(),
  name: z.string(),
  hubCompanyId: z.string().nullable(),
  personal: z.boolean(),
  permissions: z.array(z.string()).nullable(),
});
export type LinkableFolder = z.infer<typeof LinkableFolder>;

export const CompanyRef = z.object({ id: z.string(), name: z.string() });
export type CompanyRef = z.infer<typeof CompanyRef>;

export class VaultRpcError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly reason: string | null
  ) {
    super(`vault rpc ${code}${reason ? `: ${reason}` : ""}`);
    this.name = "VaultRpcError";
  }
}

const Refusal = z.object({ error: z.string(), reason: z.string().nullable().optional() });

async function rpc<S extends z.ZodType>(body: Readonly<Record<string, unknown>>, schema: S): Promise<z.infer<S>> {
  const res = await fetch("/api/vault/rpc", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const refusal = Refusal.safeParse(json);
    throw new VaultRpcError(res.status, refusal.success ? refusal.data.error : "error", refusal.success ? (refusal.data.reason ?? null) : null);
  }
  const envelope = z.object({ ok: z.literal(true), result: z.unknown() }).safeParse(json);
  const parsed = envelope.success ? schema.safeParse(envelope.data.result) : null;
  if (!parsed?.success) throw new VaultRpcError(res.status, "bad_response", null);
  return parsed.data;
}

export const companyVault = {
  listCompanyFolders: (companyId: string) => rpc({ op: "list_company_folders", companyId }, CompanyFoldersResult),
  listRootFolders: () => rpc({ op: "list_folders", parentFolderId: null }, z.array(LinkableFolder)),
  setFolderCompany: (folderId: string, companyId: string | null, cascade = true) =>
    rpc({ op: "set_folder_company", folderId, companyId, cascade }, z.object({ updatedFolderIds: z.array(z.string()) })),
  companyRef: (companyId: string) => rpc({ op: "company_ref", companyId }, CompanyRef),
};

/** The Hub page for one vault folder. */
export function vaultFolderHref(folderId: string): string {
  return `/vault/folders/${encodeURIComponent(folderId)}`;
}

/**
 * Top-level folders this user may link to `companyId`: shared (not personal), they hold
 * SHARE on it, and it is not already linked to this company.
 */
export function linkCandidates(roots: readonly LinkableFolder[], companyId: string): LinkableFolder[] {
  return roots
    .filter((f) => !f.personal && (f.permissions ?? []).includes("SHARE") && f.hubCompanyId !== companyId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Folders shown on the company card: the top-most linked ones first, nested ones after. */
export function orderForCard(folders: readonly CompanyFolderRow[]): CompanyFolderRow[] {
  return [...folders].sort((a, b) => Number(b.topMost) - Number(a.topMost) || a.path.join("/").localeCompare(b.path.join("/")));
}

/** A row the user may unlink: they hold SHARE on it (the vault re-checks). */
export function canUnlink(folder: CompanyFolderRow): boolean {
  return folder.permissions.includes("SHARE");
}
